import { useState, useEffect, useRef } from 'react';

interface UseLoadingWithRefreshReturn {
    isLoading: boolean;
    showRefreshIcon: boolean;
}

interface DataSource {
  data: any;
  error?: any;
}

export const useLoadingWithRefresh = (
  sources: DataSource | DataSource[],
  timeout: number = 10000
): UseLoadingWithRefreshReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [showRefreshIcon, setShowRefreshIcon] = useState(false);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sourcesArray = Array.isArray(sources) ? sources : [sources];
    const hasError = sourcesArray.some(source => source?.error);
    const allDataLoaded = sourcesArray.every(source => source?.data);
    const anyDataMissing = sourcesArray.some(source => !source?.data);

    // Ignore this effect if there's no data after loading ended.
    if (showRefreshIcon && sourcesArray.every(source => !source.data || source.error)) {
      return;
    }
    
    // If there's any error or missing data, start the loading timeout.
    if (hasError || anyDataMissing) {
      // Don't refresh timeout when sources change while loading.
      if (loadingTimeoutRef.current) {
        return;
      }

      setIsLoading(true);

      loadingTimeoutRef.current = setTimeout(() => {
        setShowRefreshIcon(true);
        loadingTimeoutRef.current = null;
      }, timeout);

      return;
    }
    
    // If data loaded, clear the timeout and hide the refresh icon.
    if (allDataLoaded) {
      setIsLoading(false);
      setShowRefreshIcon(false);

      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }
  }, [sources, timeout, showRefreshIcon]);

  return { isLoading, showRefreshIcon };
};
